import { useState, useEffect } from 'react';

interface ParkAssistResult {
	id: number;
	visit: {
		id: number;
		plate: {
			text: string;
		};
	};
}

export interface ParkAssistImage {
	visitId: number;
	bayId: number;
	imageUrl: string;
}

export enum FetchStatus {
	Loading = 'loading',
	Loaded = 'loaded',
	Error = 'error',
}

export default function useParkAssist({ plateText }: { plateText: string }) {
	const apiURL = import.meta.env.VITE_PARKASSIST_API;
	const slug = import.meta.env.VITE_PARKASSIST_SLUG;
	const username = import.meta.env.VITE_PARKASSIST_USERNAME;
	const password = import.meta.env.VITE_PARKASSIST_PASSWORD;
	const [imageData, setImageData] = useState<ParkAssistImage[]>([]);
	const [fetchStatus, setFetchStatus] = useState<FetchStatus>(
		FetchStatus.Loading
	);

	useEffect(() => {
		const fetchImageData = async () => {
			try {
				// Set loading state
				setFetchStatus(FetchStatus.Loading);

				// Basic Authentication credentials
				const basicAuthHeader = 'Basic ' + btoa(username + ':' + password);

				// Fetch results from the first API endpoint
				const resultsResponse = await fetch(
					`https://api.proximi.fi/parkassist?source=${apiURL}/${slug}/v2/bays/search/${plateText}?similarity>=.5&limit=3`,
					{
						headers: {
							Authorization: basicAuthHeader,
						},
					}
				);
				const results: ParkAssistResult[] = await resultsResponse.json();

				// Fetch images for each visit ID
        //@ts-ignore
				const imagePromises = results.map(async (result) => {
					const imageResponse = await fetch(
						`https://api.proximi.fi/parkassist?source=${apiURL}/${slug}/v2/visits/${result.visit.id}/entry.jpg`,
						{
							headers: {
								Authorization: basicAuthHeader,
							},
						}
					);
					const blob = await imageResponse.blob();
					const imageUrl = URL.createObjectURL(blob);
					return { visitId: result.visit.id, bayId: result.id, imageUrl };
				});

				const imageData = await Promise.all(imagePromises);
        //@ts-ignore
				setImageData(imageData);
				setFetchStatus(FetchStatus.Loaded);
			} catch (error) {
				console.error('Error fetching image data:', error);
				// Set error state
				setFetchStatus(FetchStatus.Error);
			}
		};

		if (plateText) {
			fetchImageData();
		}
	}, [apiURL, password, plateText, slug, username]);

	return [imageData, fetchStatus];
}
